// Copyright (c) Ping. All rights reserved.
//
// Defensive decode: a text message's `body` should be the bare display
// string, but some store paths (notably the thread-reply local echo)
// persist the full `{kind,payload,parent_id}` envelope JSON instead. If we
// ever receive an envelope-shaped body, decode it to the underlying text so
// NO surface (bubble or stacked/compact, on any device) renders raw JSON.
// Bare text falls straight through untouched (decode is only attempted when
// the body looks like a `com.ping.text` envelope).

// Import the parser from its concrete module rather than the `../appEvent`
// barrel: the barrel transitively pulls `deriveVariant`, and the narrower
// path keeps this hot helper (run on every text bubble) lean.
import { parseEnvelope } from '../appEvent/envelope';
import type { TextBody } from '../appEvent/types';

export function decodeBubbleBody(body: string): string {
  const t = body.trimStart();
  if (!t.startsWith('{') || !t.includes('"kind"')) return body;
  try {
    const parsed = parseEnvelope(body);
    if (parsed !== undefined && parsed.kind === 'com.ping.text') {
      const text = (parsed.body as TextBody | undefined)?.body;
      if (typeof text === 'string') return text;
    }
  } catch {
    /* not an envelope — fall through to raw */
  }
  return body;
}

/** A mention's userId + canonical display name, for tile rendering. */
export interface BubbleMention {
  readonly userId: string;
  readonly displayName: string;
}

/**
 * Pull the ranged mentions out of a `com.ping.text` envelope body so
 * the tile can render multi-word display names (`@aik 11`) as a single
 * pill. The wire keeps mentions as bare `@displayName` text in the body
 * plus a ranged `payload.mentions` side channel carrying the canonical
 * display name; we surface just the (userId, displayName) pairs and let
 * the renderer match them in the body text (longest-first), which is
 * robust to names with spaces. Returns `[]` for a bare-text body (the
 * renderer then falls back to its single-word `@handle` regex).
 */
export function decodeBubbleMentions(body: string): ReadonlyArray<BubbleMention> {
  const t = body.trimStart();
  if (!t.startsWith('{') || !t.includes('"kind"')) return [];
  try {
    const parsed = parseEnvelope(body);
    if (parsed === undefined || parsed.kind !== 'com.ping.text') return [];
    const mentions = (parsed.body as TextBody | undefined)?.mentions;
    if (mentions === undefined) return [];
    const out: BubbleMention[] = [];
    for (const m of mentions) {
      const displayName = m.displayName;
      if (
        typeof displayName === 'string' &&
        displayName.length > 0 &&
        m.userId.length > 0
      ) {
        out.push({ userId: m.userId, displayName });
      }
    }
    return out;
  } catch {
    return [];
  }
}
